const {
  LIFECYCLE,
  REQUIREMENT,
  STATUS
} = require('../../utils/guest-stay-state');

// Presentation for each requirement state: which translation to read, which
// parameters it needs, and how it looks. Kept out of the components so the copy
// for a state lives in one place and no view invents its own wording.
const ICONS = {
  [STATUS.failed]: 'exclamationTriangle',
  [STATUS.dueToday]: 'exclamationCircle',
  [STATUS.actionRequired]: 'exclamationCircle',
  [STATUS.dueLater]: 'calendar',
  [STATUS.scheduled]: 'clock',
  [STATUS.processing]: 'clock',
  [STATUS.complete]: 'checkCircle',
  [STATUS.contactManager]: 'commentAlt',
  [STATUS.unavailable]: 'questionCircle'
};

const COLORS = {
  [STATUS.failed]: 'red',
  [STATUS.dueToday]: 'yellow',
  [STATUS.actionRequired]: 'yellow',
  [STATUS.dueLater]: 'gray',
  [STATUS.scheduled]: 'blue',
  [STATUS.processing]: 'blue',
  [STATUS.complete]: 'green',
  [STATUS.contactManager]: 'gray',
  [STATUS.unavailable]: 'gray'
};

// Only a state the guest has to do something about earns a pill; everything
// else reads as a plain line so the page is skimmable.
const BADGE_STATUSES = [STATUS.dueToday, STATUS.actionRequired, STATUS.failed];

const LIFECYCLE_COLORS = {
  [LIFECYCLE.requestPending]: 'yellow',
  [LIFECYCLE.confirmed]: 'green',
  [LIFECYCLE.inProgress]: 'green',
  [LIFECYCLE.completed]: 'gray',
  [LIFECYCLE.cancelled]: 'gray',
  [LIFECYCLE.declined]: 'gray',
  [LIFECYCLE.expired]: 'gray',
  [LIFECYCLE.unknown]: 'gray'
};

const LIFECYCLE_ICONS = {
  [LIFECYCLE.requestPending]: 'clock',
  [LIFECYCLE.confirmed]: 'checkCircle',
  [LIFECYCLE.inProgress]: 'checkCircle',
  [LIFECYCLE.completed]: 'checkCircle',
  [LIFECYCLE.cancelled]: 'times',
  [LIFECYCLE.declined]: 'times',
  [LIFECYCLE.expired]: 'times',
  [LIFECYCLE.unknown]: 'questionCircle'
};

const REQUIREMENT_KEYS = {
  [REQUIREMENT.payment]: 'payment',
  [REQUIREMENT.verification]: 'verification',
  [REQUIREMENT.signature]: 'signature',
  [REQUIREMENT.securityDeposit]: 'security_deposit',
  [REQUIREMENT.guestInformation]: 'guest_information',
  [REQUIREMENT.checkInInstructions]: 'check_in_instructions'
};

// A meta line that needs a date or an amount is only usable when the projection
// actually supplied one, so each state names the variant to fall back to.
const META_VARIANTS = {
  [STATUS.dueToday]: { withAmount: 'amount', without: null },
  [STATUS.dueLater]: {
    withAmountAndDate: 'amount_on_date',
    withAmount: 'amount'
  },
  [STATUS.scheduled]: {
    withAmountAndDate: 'amount_on_date',
    withAmount: 'amount',
    without: 'no_date'
  },
  [STATUS.complete]: { withAmount: 'amount' },
  [STATUS.failed]: { withAmount: 'amount' },
  [STATUS.actionRequired]: { withAmount: 'amount' },
  [STATUS.contactManager]: { withAmount: 'amount' },
  [STATUS.notRequired]: { withAmount: 'amount' }
};

const metaVariant = item => {
  const variants = META_VARIANTS[item.status];
  if (!variants) return null;
  const date = item.scheduledDate || item.dueDate || null;
  if (item.amount && date && variants.withAmountAndDate) {
    return variants.withAmountAndDate;
  }
  if (item.amount && variants.withAmount) return variants.withAmount;
  return variants.without || null;
};

const requirementPresentation = item => ({
  translationKey: `cx.my_stay.requirements.${REQUIREMENT_KEYS[item.key]}.${
    item.status
  }`,
  metaKey: metaVariant(item)
    ? `cx.my_stay.meta.${item.status}.${metaVariant(item)}`
    : null,
  actionKey:
    item.guestActionRequired && item.route
      ? `cx.my_stay.actions.${REQUIREMENT_KEYS[item.key]}`
      : null,
  badgeKey: BADGE_STATUSES.includes(item.status)
    ? `cx.my_stay.badges.${item.status}`
    : null,
  icon: ICONS[item.status] || 'circle',
  color: COLORS[item.status] || 'gray'
});

const lifecyclePresentation = lifecycle => ({
  headlineKey: `cx.my_stay.lifecycle.${lifecycle}.headline`,
  descriptionKey: `cx.my_stay.lifecycle.${lifecycle}.description`,
  // The unknown lifecycle has no honest short label, so the pill keeps
  // whatever the receipt itself resolved rather than asserting a state.
  pillKey:
    lifecycle && lifecycle !== LIFECYCLE.unknown
      ? `cx.my_stay.status_pill.${lifecycle}`
      : null,
  color: LIFECYCLE_COLORS[lifecycle] || 'gray',
  icon: LIFECYCLE_ICONS[lifecycle] || 'questionCircle'
});

module.exports = {
  lifecyclePresentation,
  requirementPresentation
};
